import { useEffect, useState, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import DataTable from "react-data-table-component";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import moment from "moment";
import Popup from "../common/popup";
import discountIcon from "../assets/images/discount-icon.png";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export const OrderWisefavouritedReport = memo(() => {
  const { t } = useTranslation();
  const currentTime = new Date();
  const currentyear = currentTime.getFullYear();
  const currentMonth = currentTime.getUTCMonth() + 1;

  const getCurrentFiscalYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };

  const [reportType, setReportType] = useState("monthly");
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [isLoading, setIsLoading] = useState(true);

  const viewTabs = useMemo(
    () => [
      { name: t("line_view_txt"), value: "line" },
      { name: t("table_view_txt"), value: "table" },
    ],
    [t],
  );
  const [activeView, setActiveView] = useState("line");

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  const [reportData, setReportData] = useState([]);
  const [months, setMonths] = useState("9");
  const [quarter, setQuarter] = useState("");
  const [alertPopup, setAlertPopup] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [orderDetail, setOrderDetail] = useState({});
  const [searchAccount, setSearchAccount] = useState("");

  const MonthNames = [
    t("months.sep"),
    t("months.oct"),
    t("months.nov"),
    t("months.dec"),
    t("months.jan"),
    t("months.feb"),
    t("months.mar"),
    t("months.apr"),
    t("months.may"),
    t("months.jun"),
    t("months.jul"),
    t("months.aug"),
  ];

  const [orderDetailPopup, setOrderDetailPopup] = useState({
      headMoreTxt: "",
      closeButton: (
        <>
          <div className="order-top">
            <div className="head">
              <h4>
                {t("order_details_txt")} - {orderDetail?.orderNumber}
              </h4>
            </div>
            <span onClick={(e) => closeOrderDetail()} className="close"></span>
          </div>
        </>
      ),
      bodyMoreTxt: <div className="loading-buffer"></div>,
    });

  const getCurrentFiscalQuarter = () => {
    const month = new Date().getMonth() + 1;
    if (month >= 9 && month <= 11) return 1;
    if (month === 12 || month <= 2) return 2;
    if (month >= 3 && month <= 5) return 3;
    return 4;
  };

  const getAvailableQuarters = (selectedFiscalYear) => {
    const currentFiscalYear = getCurrentFiscalYear();
    const currentQuarter = getCurrentFiscalQuarter();
    return Object.entries(quarterNames)
      .filter(([quarterNumber]) => {
        if (selectedFiscalYear === currentFiscalYear) {
          return Number(quarterNumber) <= currentQuarter;
        }
        return true;
      })
      .map(([quarterNumber, label]) => (
        <option key={quarterNumber} value={quarterNumber}>
          {label}
        </option>
      ));
  };

  useEffect(() => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const fiscalQuarterMap = {
      9: 1,
      10: 1,
      11: 1,
      12: 2,
      1: 2,
      2: 2,
      3: 3,
      4: 3,
      5: 3,
      6: 4,
      7: 4,
      8: 4,
    };
    setQuarter(String(fiscalQuarterMap[month]));
  }, []);

  const noDataPlugin = {
    id: "noDataPlugin",
    beforeDraw: (chart) => {
      const { datasets } = chart.data;
      const hasData = datasets.some(
        (dataset) => dataset.data && dataset.data.length > 0,
      );
      const ctx = chart.ctx;
      ctx.save();
      ctx.clearRect(0, 0, chart.width, chart.height);
      if (!hasData) {
        const { width, height } = chart;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "16px Arial";
        ctx.fillStyle = "#eb9f9d";
        ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
      }
      ctx.restore();
    },
  };

  const quarterNames = {
    1: t("quarter_sep_nov"),
    2: t("quarter_dec_feb"),
    3: t("quarter_mar_may"),
    4: t("quarter_jun_aug"),
  };

  const getLabels = (data, type) => {
    if (type === "weekly") {
      return data.map((item) => {
        const date = new Date(item._id.week);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      });
    }
    if (type === "monthly") {
      return data.map((item) => `${item._id.month}/${item._id.year}`);
    }
    if (type === "quarterly") {
      return data.map((item) => quarterNames[item._id.quarter]);
    }
    return data.map((item) => item._id.year);
  };

  useEffect(() => {
    setIsLoading(true);
    http
      .post(BaseUrl + "admin/orderwisefavouritereport", {
        reportType,
        period,
      })
      .then((response) => {
        const data = response?.data?.data || [];
        setReportData(data);
        const labels = getLabels(data, reportType);
        const dataset = {
          label: t("favourite_orders_txt"),
          data: data.map((item) => item.orderCount),
          revenueData: data.map((item) => item.amount),
          borderColor: "rgb(54, 162, 235)",
          backgroundColor: "rgba(54, 162, 235, 0.3)",
          tension: 0.3,
        };
        setChartData({
          labels,
          datasets: [dataset],
        });
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, [reportType, period]);

  const tableData = useMemo(() => {
    const filtered =
      reportType === "monthly"
        ? reportData.filter((bucket) => bucket._id.month === Number(months))
        : reportType === "quarterly"
          ? reportData.filter(
              (bucket) => bucket._id.quarter === Number(quarter),
            )
          : reportData;
    const orders = filtered.flatMap((bucket) => bucket.orders || []);
    const searchText = searchAccount.trim().toLowerCase();
    const searched = searchText
      ? orders.filter((order) =>
          (order.accountCode || "").toString().toLowerCase().includes(searchText),
        )
      : orders;
    return [...searched].sort(
      (a, b) => Number(b.orderNumber) - Number(a.orderNumber),
    );
  }, [reportData, reportType, months, quarter, searchAccount]);

  const customStyles = {
    rows: { style: { minHeight: "25px" } },
    headCells: { style: { padding: "8px" } },
    cells: { style: { padding: "8px" } },
  };

  const paginationComponentOptions = {
    rowsPerPageText: "",
    rangeSeparatorText: "",
    selectAllRowsItem: true,
    selectAllRowsItemText: "",
    noRowsPerPage: "",
  };

  const getOrderDetails = (stripelog) => {
    setOrderDetailPopup({
      headMoreTxt: "",
      closeButton: (
        <>
          <div className="order-top">
            <div className="head">
              <h4>
                {t("order_details_txt")} - {stripelog.orderNumber}
              </h4>
            </div>
            <span onClick={(e) => closeOrderDetail()} className="close"></span>
          </div>
        </>
      ),
      bodyMoreTxt: <div className="loading-buffer"></div>,
    });
    setOrderDetail({ orderNumber: stripelog.orderNumber });
    setShowOrderDetail(true);
    var numberOfAjaxCAllPending = 0;
    var timeInterval = 20000;
    var current = this;
    var loginDetails = {};
    if (sessionStorage.getItem("authData")) {
      loginDetails = JSON.parse(sessionStorage.getItem("authData"));
    }
    var URL = BaseUrl + "admin/getorderdetails/" + stripelog.orderNumber;
    http
      .get(URL)
      .then((response) => {
        numberOfAjaxCAllPending++;
        let responseData = [];
        responseData = response.data.data[0];
        setIsLoading(false);
        viewOrderlogDetails(responseData);
        setAlertPopup(false);
      })
      .catch((err) => {
        console.log(err);
        setAlertPopup(false);
        setIsLoading(false);
      });
  };

  const vatAmountCalcuation = (order) => {
    var totalAmount = 0;
    order.items.map((item, value) => {
      if (item.TaxCode !== "" && item.TaxCode === "T1") {
        totalAmount += item.LinePrice;
      }
    });
    return totalAmount;
  };

  const viewOrderlogDetails = (order) => {
    var ordertotal = 0;
    var subTotal = 0;
    setAlertPopup(false);
    order.items.map((item) => {
      ordertotal += item.LinePrice;
    });
    order.items.map((item) => {
      subTotal += item.UnitPrice * item.QuantityOrdered;
    });
    var vatTotalAmount = vatAmountCalcuation(order);
    var vatAmount = 0;
    if (vatTotalAmount > 0) {
      vatAmount = (vatTotalAmount / 100) * 20;
      ordertotal = ordertotal + vatAmount;
    }
    if (order.accountEmail !== undefined) {
      setOrderDetail((prev) => ({
        ...prev,
        oaccountName: order.accountName,
        oaccountEmail: order.accountEmail,
        oaccountAddress: order.accountAddress,
      }));
      createPopup(order, subTotal, ordertotal, vatAmount, vatTotalAmount);
    } else {
      var loginDetails = {};
      if (sessionStorage.getItem("authData")) {
        loginDetails = JSON.parse(sessionStorage.getItem("authData"));
      }
      let URL = BaseUrl + "admin/account/" + order.accountCode;
      http
        .get(URL)
        .then((response) => {
          let responseData = [];
          responseData = response.data.data;
          let addressFields = [
            responseData.accountAddress1,
            responseData.accountAddress2,
            responseData.accountAddress3,
            responseData.accountAddress4,
            responseData.accountAddress5,
          ];
          setOrderDetail((prev) => ({
            ...prev,
            oaccountName: responseData.accountName,
            oaccountEmail: responseData.accountEmail,
            oaccountAddress: addressFields
              .filter((field) => field !== undefined && field != "")
              .join(", "),
          }));
          createPopup(order, subTotal, ordertotal, vatAmount, vatTotalAmount);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    }
  };

  const closeOrderDetail = (e) => {
    setOrderDetail({});
    setShowOrderDetail(false);
  };

  const priceFormat = (price) => {
    return price.toLocaleString("en-US", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
    });
  };

  const str_replace = (product_name) => {
    if (product_name !== "") {
      product_name = product_name.replaceAll("amp;", "&");
      product_name = product_name.replaceAll("apos;'", "'' ");
      product_name = product_name.replaceAll("quot;'", "'' ");
      return product_name;
    } else {
      return product_name;
    }
  };

  const createPopup = (order, subTotal, ordertotal, vatAmount, vatTotalAmount) => {
    // Sort order items to move those with UnitPrice 0 to the end
    const sortedItems = [...order.items].sort((a, b) => {
      if (a.UnitPrice === 0 && b.UnitPrice !== 0) return 1;
      if (a.UnitPrice !== 0 && b.UnitPrice === 0) return -1;
      return 0;
    });
    let headMoreTxt = (
      <>
        <div className="order-top">
          <div className="bottom order-flex">
            <div className="order_no_details">
              <h3>
                {t("order_no_txt")} - {order.OrderNumber}
              </h3>
              <p>
                {t("order_placed_on_txt")}:{" "}
                <span className="date">
                  {" "}
                  {moment(order.createdAt).format("DD/MM/YYYY hh:mm A")}
                </span>{" "}
              </p>
              <p>
                {t("order_deleivery_on_txt")}:{" "}
                <span className="date"> {order.DateRequired} </span>
              </p>
              {order.PaymentMethod === "A" && (
                <p>
                  {t("payment_method_txt")}:{" "}
                  <span className="pay">
                    {" "}
                    {order.OrderPaid === "YES"
                      ? t("paid_pay_later_txt")
                      : t("pay_later_txt")}
                  </span>
                </p>
              )}
              {order.PaymentMethod === "K" && (
                <p>
                  {t("payment_method_txt")}:{" "}
                  <span className="pay">{t("online_payment_txt")}</span>
                </p>
              )}
              <p>
                {t("status_txt")}:
                <span className="pay"> {order.OrderStatus}</span>
              </p>
            </div>
            <div className="order_account_details">
              <p>
                {t("name_txt")}:{" "}
                <span className="date">{order.accountName}</span>{" "}
              </p>
              <p>
                {t("account_code_txt")}:{" "}
                <span className="date">{order.accountCode}</span>{" "}
              </p>
              <p>
                {t("email_address_txt")}:{" "}
                <span className="pay"> {order.accountEmail}</span>
              </p>
              <p>
                {t("address_txt")}:{" "}
                <span className="pay">{order.accountAddress}</span>
              </p>
              {order.PODetails && (
                <p>
                  {t("po_details_txt")}:{" "}
                  <span className="pay">{order.PODetails}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </>
    );
    let closebtn = (
      <>
        <div className="pop-header-top order-top">
          <div className="head">
            <h4>
              {t("order_details_txt")} - {order.OrderNumber}{" "}
            </h4>
          </div>
          <span onClick={(e) => closeOrderDetail()} className="close"></span>
        </div>
      </>
    );
    let bodyMoreTxt = (
      <>
        <div className="modal-table">
          <h4 className="item-title">{t("ordered_items_txt")}</h4>
          <div className="checkout-table">
            <div className="table_overflow">
              <table>
                <thead>
                  <tr>
                    <td>{t("product_txt")}</td>
                    <td>{t("unit_of_sale_txt")}</td>
                    <td>{t("quantity_txt")}</td>
                    <td align="center">{t("price_txt")}</td>
                    <td align="right">{t("total_txt")}</td>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, index) => {
                    return (
                      item.QuantityOrdered > 0 && (
                        <tr
                          key={index}
                          className={item.UnitPrice === 0 ? "free-prod-bg" : ""}
                        >
                          <td
                            data-title={t("product_txt")}
                            className="data-product-hide"
                          >
                            {str_replace(item.Description)} (
                            {item.ProductNumber.includes("_")
                              ? item.ProductNumber.substring(
                                  0,
                                  item.ProductNumber.indexOf("_"),
                                )
                              : item.ProductNumber}
                            )
                            {item.TaxCode !== "" && item.TaxCode === "T1" && (
                              <span className="color-red">*</span>
                            )}
                            {item.OfferFlag == "true" && (
                              <span className="offer-tag-wrap">
                                <span className="offer-tag">
                                  <img
                                    src={discountIcon}
                                    alt={t("offer_txt")}
                                    className="tag-icon"
                                  />
                                  {t("special_offer_txt")}
                                </span>
                              </span>
                            )}
                          </td>
                          <td data-title={t("unit_of_sale_txt")}>
                            {item.PricingUnit}
                          </td>
                          <td data-title={t("quantity_txt")}>
                            {item.QuantityOrdered}
                          </td>
                          <td data-title={t("price_txt")} align="center">
                            {priceFormat(parseFloat(item.UnitPrice))}
                          </td>
                          <td data-title={t("total_txt")} align="right">
                            {priceFormat(parseFloat(item.LinePrice))}
                          </td>
                        </tr>
                      )
                    );
                  })}
                </tbody>
                <thead className="ord-grand-total">
                  {order.OrderNote !== "" && order.OrderNote !== null && (
                    <tr>
                      <td align="left" width={120} colSpan="7">
                        {t("order_notes_txt")}:{" "}
                        <span className="order_remarks"> {order.OrderNote}</span>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td align="right" width={120} colSpan="4">
                      {t("subtotal_txt")}:
                    </td>
                    <td align="right">{priceFormat(subTotal)}</td>
                  </tr>
                  {vatAmount > 0 && (
                    <tr>
                      <td
                        align="right"
                        width={120}
                        colSpan="4"
                        className="td-vat-fee"
                      >
                        <span className="strong">
                          {t("vat_txt")}{" "}
                          <span className="vat-txt">
                            (20% of{" "}
                            <span className="vat-amt">
                              {priceFormat(vatTotalAmount)}
                            </span>
                            )<span className="color-red">*</span>:
                          </span>
                        </span>
                      </td>
                      <td className="cart-prod-vat" align="right">
                        {priceFormat(vatAmount)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td align="right" width={120} colSpan="4">
                      {t("shipping_txt")}:
                    </td>
                    <td align="right">
                      <span className="color-red">{t("free_txt")}</span>
                    </td>
                  </tr>
                  <tr>
                    <td align="right" width={120} colSpan="4">
                      {t("grand_total_txt")}:
                    </td>
                    <td align="right">{priceFormat(ordertotal)}</td>
                  </tr>
                </thead>
              </table>
            </div>
          </div>
        </div>
      </>
    );
    setOrderDetailPopup({
      headMoreTxt: headMoreTxt,
      closeButton: closebtn,
      bodyMoreTxt: bodyMoreTxt,
    });
  };

  const columns = useMemo(
    () => [
      {
        name: t("account_code_txt"),
        selector: (row) => row.accountCode,
        sortable: true,
      },
      {
        name: t("account_name_txt"),
        selector: (row) => row.accountName,
        sortable: false,
      },
      {
        name: t("email_address_txt"),
        selector: (row) => row.accountEmail,
        sortable: false,
      },
      {
        name: t("order_id_txt"),
        selector: (row) => row.orderNumber,
        cell: (row) => (
          <div style={{ display: "flex" }}>
            <a
              className="button"
              data-title={t("view_order_details_txt")}
              onClick={() => getOrderDetails(row)}
              >
                {row.orderNumber}
            </a>
          </div>
        ),
        sortable: true,
      },
      {
        name: t("ordered_date_txt"),
        selector: (row) => row.dateOrdered,
        sortable: true,
      },
      {
        name: t("ordered_amount_txt"),
        selector: (row) => priceFormat(parseFloat(row.revenue || 0)),
        sortable: false,
      },
    ],
    [t],
  );

  const changeHandler = (e) => {
    setReportType(e.target.value);
  };

  const changePeriodHandler = (e) => {
    setPeriod(e.target.value);
  };

  const changeMonthHandler = (e) => {
    setMonths(e.target.value);
  };

  const changeSearchHandler = (e) => {
    setSearchAccount(e.target.value);
  };
  const currentFiscalYear = getCurrentFiscalYear();
  let visibleMonthNames = MonthNames;
  if (period === currentFiscalYear) {
    const fiscalIndex = (new Date().getMonth() - 8 + 12) % 12;
    visibleMonthNames = MonthNames.slice(0, fiscalIndex + 1);
  }

  const options = [];

  for (let i = currentyear; i >= 2023; i--) {
    if (i === currentyear && currentMonth < 9) {
      continue;
    }
    options.push(
      <option key={`${i}-${i + 1}`} value={`${i}-${i + 1}`}>
        {`${i}-${i + 1}`}
      </option>,
    );
  }

  function centerSinglePointDatasets(chartData) {
    const labels = chartData.labels;
    const updatedDatasets = chartData.datasets.map((dataset) => {
      const validPoints = dataset.data
        .map((val, i) => ({ val, i }))
        .filter(({ val }) => val?.y > 0);
      if (validPoints.length === 1) {
        const onlyIndex = validPoints[0].i;
        const centeredData = labels.map((_, i) =>
          i === onlyIndex ? validPoints[0].val : null,
        );
        return {
          ...dataset,
          data: centeredData,
          spanGaps: false,
          pointRadius: 4,
          pointHoverRadius: 6,
        };
      }
      return dataset;
    });
    return {
      ...chartData,
      datasets: updatedDatasets,
    };
  }

  const totalOrdersCount = useMemo(
    () =>
      chartData.datasets[0]?.data.reduce(
        (sum, val) => sum + (Number(val) || 0),
        0,
      ) || 0,
    [chartData],
  );

  const totalRevenue = useMemo(
    () =>
      chartData.datasets[0]?.revenueData?.reduce(
        (sum, val) => sum + (Number(val) || 0),
        0,
      ) || 0,
    [chartData],
  );

  const tableTotalRevenue = useMemo(
    () =>
      tableData.reduce((sum, row) => sum + (Number(row.revenue) || 0), 0),
    [tableData],
  );

  const loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div
      className={`chart-container chart-order-wise-favourite-report ${loadingbuffer}`}
      style={{ verticalAlign: "top" }}
    >
      <div className="chart-Hdr">
        <div className="chart-Hdr-txt">
          <h2>{t("order_wise_favourite_report_txt")}</h2>
        </div>
        <div className="chart-fltr-Hldr">
        <div className="chart-fltr">
          {activeView === "table" && (
          <input
            type="text"
            value={searchAccount}
            onChange={changeSearchHandler}
            placeholder={t("search_by_account_code_txt")}
            style={{ width: "200px" }}
          />
          )}
          <select
            style={{ width: "150px" }}
            value={activeView}
            onChange={(e) => setActiveView(e.target.value)}
          >
            {viewTabs.map((tab) => (
              <option key={tab.value} value={tab.value}>
                {tab.name}
              </option>
            ))}
          </select>
          <select
            style={{ width: "150px" }}
            value={reportType}
            onChange={changeHandler}
          >
            <option value="weekly">{t("current_week_txt")}</option>
            <option value="monthly">{t("monthly_txt")}</option>
            <option value="quarterly">{t("quarterly_txt")}</option>
            <option value="yearly">{t("fy_data_txt")}</option>
          </select>
          {activeView === "table" && reportType === "monthly" && (
            <select value={months} onChange={changeMonthHandler}>
              {visibleMonthNames.map((monthName, index) => {
                const monthNumber = ((index + 8) % 12) + 1;
                return (
                  <option key={monthNumber} value={monthNumber}>
                    {monthName}
                  </option>
                );
              })}
            </select>
          )}
          {activeView === "table" && reportType === "quarterly" && (
            <select
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
            >
              {getAvailableQuarters(period)}
            </select>
          )}
          {(reportType === "monthly" ||
            reportType === "quarterly" ||
            reportType === "yearly") && (
            <select
              style={{ width: "170px" }}
              value={period}
              onChange={changePeriodHandler}
            >
              {options}
            </select>
          )}
        </div>
        </div>
        <div className="fltr-sub-val">
          {activeView === "table" ? (
            tableData.length > 0 && (
              <span className="fltr-val">
                {t("total_records")?.replace("{{count}}", tableData.length)}
                {"  |  "}
                {t("total_revenue_txt")}: {priceFormat(tableTotalRevenue)}
              </span>
            )
          ) : (
            <span className="fltr-val">
              {t("total_order_count_text_txt")} {totalOrdersCount}
              {"  |  "}
              {t("total_revenue_label_txt")} {priceFormat(totalRevenue)}
            </span>
          )}
        </div>
      </div>
      {activeView === "line" && (
       <p className="note-text">{t("note_text_for_order_report")}</p>
      )}
      <br />
      {activeView === "table" && (
        <DataTable
          columns={columns}
          data={tableData}
          customStyles={customStyles}
          noDataComponent={
            <div className="no-records-found">
              {t("no_data_filtered_search_txt")}
            </div>
          }
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 40, 60, 80, 100]}
          paginationComponentOptions={paginationComponentOptions}
        />
      )}
      {activeView === "line" && chartData && (
        <Line
          data={centerSinglePointDatasets(chartData)}
          plugins={[noDataPlugin]}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false,
              },
              title: {
                display: false,
              },
              datalabels: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (tooltipItem) => {
                    const revenue =
                      tooltipItem.dataset.revenueData?.[
                        tooltipItem.dataIndex
                      ] || 0;

                    return [
                      `${t("no_of_orders_txt")} (#): ${tooltipItem.raw}`,
                      `${t("total_revenue_txt")}: ${Number(revenue).toLocaleString(
                        "en-GB",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}`,
                    ];
                  },
                },
              },
            },
            scales: {
              x: {
                offset: true,
                grid: {
                  display: false,
                },
                ticks: {
                  align: "center",
                },
              },
              y: {
                beginAtZero: true,
                title: {
                  display: false,
                },
              },
            },
          }}
        />
      )}
      {showOrderDetail === true && (
        <Popup
          extClass="order-pop my-order-details-pop"
          headmoreText
          headMoreTxt={orderDetailPopup?.headMoreTxt}
          closebtn={orderDetailPopup?.closeButton}
          bodyMoreTxt={orderDetailPopup?.bodyMoreTxt}
        />
      )}
    </div>
  );
});
